/**
 * Quinvoice REST API Client
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  window.location.hostname === 'localhost' ? 'http://localhost:8000/api' : '/api'
);

class ApiClient {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  getToken() {
    return localStorage.getItem('quinvoice_token');
  }

  setToken(token) {
    if (token) {
      localStorage.setItem('quinvoice_token', token);
    } else {
      localStorage.removeItem('quinvoice_token');
    }
  }

  getAdminBackupToken() {
    return localStorage.getItem('quinvoice_admin_backup_token');
  }

  setAdminBackupToken(token) {
    if (token) {
      localStorage.setItem('quinvoice_admin_backup_token', token);
    } else {
      localStorage.removeItem('quinvoice_admin_backup_token');
    }
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
    const token = this.getToken();

    const headers = {
      'Accept': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...options.headers,
    };

    if (!(options.body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const config = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);
      
      // Handle Unauthorized
      if (response.status === 401) {
        // If we are impersonating, maybe restore admin token, otherwise logout
        const event = new CustomEvent('quinvoice:unauthorized');
        window.dispatchEvent(event);
      }

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      let data = {};
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else if (response.status !== 204) {
        const text = await response.text();
        try {
          data = JSON.parse(text);
        } catch {
          data = { message: text };
        }
      }

      if (!response.ok) {
        const error = new Error(data.message || `Request failed with status ${response.status}`);
        error.status = response.status;
        error.data = data;
        error.errors = data.errors;
        throw error;
      }

      return data;
    } catch (err) {
      throw err;
    }
  }

  get(endpoint, params = {}) {
    const query = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        query.append(key, params[key]);
      }
    });
    const queryString = query.toString() ? `?${query.toString()}` : '';
    return this.request(`${endpoint}${queryString}`, { method: 'GET' });
  }

  post(endpoint, body = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  postForm(endpoint, formData) {
    return this.request(endpoint, {
      method: 'POST',
      body: formData,
    });
  }

  // Blob download helper (e.g. for PDF)
  async downloadBlob(endpoint, filename) {
    const url = `${this.baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
    const token = this.getToken();

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/pdf, application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });

    if (!response.ok) {
      throw new Error('Error al descargar el archivo PDF.');
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'document.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  }

  // View PDF in new tab
  getPdfUrl(type, id) {
    const token = this.getToken();
    return `${this.baseUrl}/${type}/${id}/pdf?token=${encodeURIComponent(token || '')}`;
  }
}

export const api = new ApiClient();

/* Service Modules */

export const authService = {
  login: (credentials) => api.post('/login', { ...credentials, device_name: 'Quinvoice Web App' }),
  register: (data) => api.post('/register', { ...data, device_name: 'Quinvoice Web App' }),
  logout: () => api.post('/logout'),
  getUser: () => api.get('/user'),
  sendVerificationEmail: () => api.post('/email/verification-notification'),
};

export const profileService = {
  update: (formData) => api.postForm('/user/update', formData),
  changePassword: (data) => api.post('/account/password', data),
  deleteAccount: (data) => api.post('/account/delete', data),
  syncSubscription: () => api.post('/user/subscription/sync'),
};

export const dashboardService = {
  getStats: () => api.get('/dashboard'),
};

export const currenciesService = {
  getAll: () => api.get('/currencies'),
};

export const customersService = {
  list: (params) => api.get('/customers', params),
  create: (data) => api.post('/customers/store', data),
  update: (id, data) => api.post(`/customers/update/${id}`, data),
  delete: (id) => api.post(`/customers/delete/${id}`),
};

export const projectsService = {
  list: (params) => api.get('/projects', params),
  create: (data) => api.post('/projects/store', data),
  update: (id, data) => api.post(`/projects/update/${id}`, data),
  delete: (id) => api.post(`/projects/delete/${id}`),
};

export const productsService = {
  list: (params) => api.get('/products', params),
  create: (data) => api.post('/products/store', data),
  get: (id) => api.get(`/products/${id}`),
  update: (id, data) => api.post(`/products/update/${id}`, data),
  delete: (id) => api.post(`/products/delete/${id}`),
  adjustStock: (id, data) => api.post(`/products/adjust-stock/${id}`, data),
  movements: (id) => api.get(`/products/movements/${id}`),
};

export const quotesService = {
  list: (params) => api.get('/quotes', params),
  getCreateData: () => api.get('/quotes/create'),
  create: (data) => api.post('/quotes/store', data),
  get: (id) => api.get(`/quotes/${id}`),
  update: (id, data) => api.post(`/quotes/update/${id}`, data),
  delete: (id) => api.post(`/quotes/delete/${id}`),
  convert: (id) => api.post(`/quotes/convert/${id}`),
  downloadPdf: (id, number) => api.downloadBlob(`/quotes/${id}/pdf`, `Cotizacion-${number || id}.pdf`),
};

export const invoicesService = {
  list: (params) => api.get('/invoices', params),
  getCreateData: () => api.get('/invoices/create'),
  create: (data) => api.post('/invoices/store', data),
  get: (id) => api.get(`/invoices/${id}`),
  update: (id, data) => api.post(`/invoices/update/${id}`, data),
  delete: (id) => api.post(`/invoices/delete/${id}`),
  pay: (id, data) => api.post(`/invoices/pay/${id}`, data),
  deletePayment: (id, payId) => api.post(`/invoices/pay/delete/${id}/${payId}`),
  changeStatus: (id, status) => api.post(`/invoices/status/${id}/${status}`),
  downloadPdf: (id, number) => api.downloadBlob(`/invoices/${id}/pdf`, `Factura-${number || id}.pdf`),
};

export const subscriptionsService = {
  getPlans: () => api.get('/subscriptions/plans'),
  getConfig: () => api.get('/paypal/config'),
  success: (subscriptionId) => api.post('/user/subscription/success', { subscription_id: subscriptionId }),
  cancel: () => api.post('/user/subscription/cancel'),
};

export const adminService = {
  dashboard: () => api.get('/admin/dashboard'),
  users: (params) => api.get('/admin/users', params),
  createUser: (data) => api.post('/admin/users/store', data),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.post(`/admin/users/${id}/update`, data),
  banUser: (id, reason) => api.post(`/admin/users/${id}/ban`, { reason }),
  unbanUser: (id) => api.post(`/admin/users/${id}/unban`),
  restoreUser: (id) => api.post(`/admin/users/${id}/restore`),
  deleteUser: (id, force = false) => api.post(`/admin/users/${id}/delete`, { force }),
  impersonate: (id) => api.post(`/admin/users/${id}/impersonate`),
  invoices: (params) => api.get('/admin/invoices', params),
  getInvoice: (id) => api.get(`/admin/invoices/${id}`),
  deleteInvoice: (id) => api.post(`/admin/invoices/${id}/delete`),
  quotes: (params) => api.get('/admin/quotes', params),
  getQuote: (id) => api.get(`/admin/quotes/${id}`),
  deleteQuote: (id) => api.post(`/admin/quotes/${id}/delete`),
  customers: (params) => api.get('/admin/customers', params),
  deleteCustomer: (id) => api.post(`/admin/customers/${id}/delete`),
  projects: (params) => api.get('/admin/projects', params),
  deleteProject: (id) => api.post(`/admin/projects/${id}/delete`),
  products: (params) => api.get('/admin/products', params),
  deleteProduct: (id) => api.post(`/admin/products/${id}/delete`),
  movements: (params) => api.get('/admin/movements', params),
  subscriptions: (params) => api.get('/admin/subscriptions', params),
  toggleSubscription: (id) => api.post(`/admin/subscriptions/${id}/toggle`),
  currencies: () => api.get('/admin/currencies'),
  createCurrency: (data) => api.post('/admin/currencies/store', data),
  updateCurrency: (id, data) => api.post(`/admin/currencies/${id}/update`, data),
  deleteCurrency: (id) => api.post(`/admin/currencies/${id}/delete`),
  system: () => api.get('/admin/system'),
  clearCache: () => api.post('/admin/system/cache'),
};
